// The weekly challenge's colors (scenario Q9, B-619): the banner the page and
// the League page's card open with, in the week's two positions' colors over
// a field's yard lines with a football drawn in them, and a pip for each of
// an entry's picks. The colors are the positions' own, handed to the
// stylesheet as data (`Colored`), so a week of quarterbacks and backs looks
// different from one of receivers and tight ends.

import type { ReactNode } from 'react';
import { FootballArt } from '../../kit/art';
import { Colored } from '../../kit/ui';
import '../../styles/screens/challenge.css';

/** A position's color token, as the position pill reads it. */
export function positionColor(position: string): string {
  return `var(--fsp-pos-${position.toLowerCase()}, var(--fsp-accent))`;
}

/** The week's banner: its children over the two positions' colors, the drawing at its end. */
export function ChallengeBanner({
  positions,
  compact = false,
  children,
}: {
  /** The week's drawn positions; none before it opens, which draws it in the accent. */
  positions: readonly string[];
  /** The League page's card: smaller, the drawing too. */
  compact?: boolean;
  children: ReactNode;
}) {
  const a = positions[0] === undefined ? 'var(--fsp-accent)' : positionColor(positions[0]);
  const b = positions[1] === undefined ? a : positionColor(positions[1]);
  return (
    <Colored
      className="fsp-challenge-banner"
      data-compact={compact ? 'true' : undefined}
      colors={{ '--fsp-art-a': a, '--fsp-art-b': b }}
    >
      <div className="fsp-challenge-banner-body">{children}</div>
      <span className="fsp-challenge-banner-art">
        <FootballArt size={compact ? 64 : 112} />
      </span>
    </Colored>
  );
}

/**
 * A pip for each slot of an entry, in its position's color, filled once
 * picked and crowned on the captain's; the count in words beside it.
 */
export function ChallengePips({
  slots,
  picks,
  captain,
}: {
  /** Each slot's position, in slot order. */
  slots: readonly string[];
  picks: readonly (string | null)[];
  captain: string | null;
}) {
  const picked = picks.filter((p) => p !== null).length;
  return (
    <div className="fsp-challenge-pips">
      <span className="fsp-challenge-pips-row" aria-hidden>
        {slots.map((position, i) => (
          <Colored
            key={i}
            inline
            className="fsp-challenge-pip"
            data-on={picks[i] != null ? 'true' : undefined}
            data-captain={picks[i] != null && picks[i] === captain ? 'true' : undefined}
            colors={{ '--fsp-challenge-pos': positionColor(position) }}
          />
        ))}
      </span>
      <span className="fsp-challenge-pips-words">
        {picked} of {slots.length} picked
        {picked > 0 ? (captain !== null ? ', captain chosen' : ', no captain yet') : ''}
      </span>
    </div>
  );
}
